/**
 * Страница вопроса клиента `/knowledge/q/<id>` (внутри «Базы знаний», вкладка «Вопросы клиентов»).
 * Хронология «вопрос → ответ»: шапка (частота за период, каналы, мой статус), как спрашивают клиенты,
 * мой ответ голосом или текстом, затем сам ответ — карточки знаний, привязанные к вопросу, и ответы
 * сотрудников по времени (текст, записи голоса с расшифровкой, правки). Сотрудник видит свои ответы и
 * слова клиентов; редактор — все ответы и отбирает ответы «годится для карточки» (карточку по отбору
 * собираем в чате сессии). Где задавали вопрос — кнопки «Чаты · N» / «Звонки · N» в шапке: отбор
 * `?kbq=` в «Чатах» (владелец 27.10.26: вместо списка случаев — переписка и звонки целиком).
 *
 * Очередь (`?queue=1`): список «Вопросов клиентов» кладёт id подборки в sessionStorage («Пройти
 * очередь»), здесь — «Назад / Следующий», позиция «N из M», клавиши R / J / K, Esc — к списку.
 * Спека `docs/specs/2026-10-26-kb-questions-ux-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { AnswerForm, myAnswer } from "@/components/knowledge/AnswerForm";
import { ApiFailure } from "@/lib/api";
import { AnswerList } from "@/components/knowledge/AnswerList";
import { Hits } from "@/components/knowledge/Hits";
import { channelName, KINDS, loadAnswers, loadMeta, loadQuestion, type MineStatus } from "@/lib/knowledge";
import type { CurrentUser } from "@/lib/providers";
import { box, errText, SyncBadge } from "@/pages/KnowledgeCardPage";

/** Очередь «Пройти очередь»: id вопросов подборки и адрес списка, куда вернуться. */
export type Queue = { ids: number[]; back: string };
const QUEUE = "kb-questions-queue";

export function saveQueue(q: Queue) {
  try {
    sessionStorage.setItem(QUEUE, JSON.stringify(q));
  } catch {
    // без хранилища очередь живёт только в адресе текущего вопроса — «Следующий» не появится
  }
}
export function readQueue(): Queue | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(QUEUE) ?? "null") as Queue | null;
    return v && Array.isArray(v.ids) ? v : null;
  } catch {
    return null;
  }
}

export const MINE: Record<MineStatus, string> = {
  none: "без моего ответа",
  open: "мой ответ не полный",
  done: "мой ответ полный",
  used: "мой ответ в карточке",
};
export const mineTone = (s: MineStatus) => (s === "done" || s === "used" ? "text-ok" : s === "open" ? "text-warn" : "text-fg-faint");

const ПОДСКАЗКА: Record<string, string> = {
  live: "Живые данные: наличие, цена конкретной вещи меняются. Опишите, что уточняете у клиента и где проверяете — сами остатки в ответ не пишите.",
  action: "Действие в CRM: опишите порядок — что проверяете и что делаете (запись, бронь), а не обещайте время без проверки.",
};

const pill = (on: boolean) =>
  `rounded-full border px-2.5 py-0.5 text-[12px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;

export default function KnowledgeQuestionPage() {
  const { questionId } = useParams();
  const id = Number(questionId);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { data: me } = useGetIdentity<CurrentUser>();
  const days = Number(params.get("days") ?? 30);
  const inQueue = params.get("queue") === "1";
  const queue = inQueue ? readQueue() : null;
  const pos = queue ? queue.ids.indexOf(id) : -1;
  const back = `/knowledge?${queue?.back ?? params.get("back") ?? "tab=questions"}`;

  const meta = useQuery({ queryKey: ["kb", "meta"], queryFn: loadMeta });
  const q = useQuery({ queryKey: ["kb", "question", id, days], queryFn: () => loadQuestion(id, days), enabled: id > 0 });
  const answers = useQuery({ queryKey: ["kb", "answers", id], queryFn: () => loadAnswers(id), enabled: id > 0 });

  // слитый вопрос — на главный, с тем же периодом и очередью
  useEffect(() => {
    if (q.data?.merged_into) nav(`/knowledge/q/${q.data.merged_into}?${params.toString()}`, { replace: true });
  }, [q.data?.merged_into, nav, params]);

  // Esc вне полей — к списку (форма сама сохраняет текст при размонтировании)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Escape" || e.defaultPrevented) return;
      const t = e.target;
      if (t instanceof HTMLElement && t.closest("input,textarea,select,[contenteditable='true'],dialog")) return;
      nav(back);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [nav, back]);

  const go = (to: number) => {
    if (!queue) return;
    if (to >= queue.ids.length) {
      nav(`${back}&queue_done=${queue.ids.length}`);
      return;
    }
    const p = new URLSearchParams(params);
    nav(`/knowledge/q/${queue.ids[to]}?${p.toString()}`);
    window.scrollTo({ top: 0 });
  };

  if (!(id > 0)) return <div className="p-5 text-fg-muted">Нет такого вопроса.</div>;
  if (q.error) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 p-3 md:p-5">
        <Link to={back} className="text-[12.5px] text-fg-muted hover:text-fg">
          ← Вопросы клиентов
        </Link>
        <div className={`${box} p-4 text-[13px]`}>
          {q.error instanceof ApiFailure && q.error.status === 404
            ? "Вопрос не найден — возможно, его слили с другим или ссылка устарела."
            : errText(q.error)}
        </div>
      </div>
    );
  }
  if (!q.data || q.data.merged_into) return <div className="p-5 text-fg-muted">Загрузка…</div>;
  const d = q.data;
  const редактор = d.can_edit;
  const чаты = !!me?.permissions.includes("client_chats");
  const категория = meta.data?.categories.find((c) => c.code === d.category)?.label ?? (d.category || d.topic);
  const всеОтветы = answers.data ?? [];
  const мой = myAnswer(всеОтветы, me?.id);
  const прочие = всеОтветы.filter((a) => a.id !== мой?.id && (a.text || a.voices.length > 0));
  const отобрано = прочие.filter((a) => a.selected && a.status === "new").length + (мой?.selected ? 1 : 0);
  const answerMode = params.get("answer");

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-3 md:p-5">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <Link to={back} className="flex items-center gap-1 text-fg-muted hover:text-fg">
          <ArrowLeft size={14} /> Вопросы клиентов
        </Link>
        <span className="ml-auto text-fg-faint">
          {queue && pos >= 0 ? `Очередь · ${pos + 1} из ${queue.ids.length}` : `Вопрос №${d.id}`}
        </span>
      </div>

      <header className="space-y-1.5 border-b border-border pb-3">
        <h1 className="text-[18px] font-semibold leading-snug text-fg">{d.text}</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-fg-muted">
          <span className="text-fg">{категория}</span>
          {d.kind && d.kind !== "knowledge" && (
            <span className="rounded border border-warn bg-warn/10 px-1.5 text-fg">{meta.data?.question_kinds[d.kind] ?? d.kind}</span>
          )}
          <span>
            <b className="tabular-nums text-fg">{d.hits}</b> диалогов за {days} дн.
            {d.hits_all > d.hits && <span className="text-fg-faint"> · всего {d.hits_all}</span>}
          </span>
          {d.unanswered > 0 && <span className="text-warn">без полного ответа в диалогах: {d.unanswered}</span>}
          {d.channels.length > 0 && <span>{d.channels.map(channelName).join(", ")}</span>}
          {d.last && <span className="text-fg-faint">последний {d.last.slice(8, 10)}.{d.last.slice(5, 7)}</span>}
          <span className={mineTone(d.mine_status)}>{MINE[d.mine_status]}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              type="button"
              className={pill(days === n)}
              onClick={() => {
                const p = new URLSearchParams(params);
                p.set("days", String(n));
                setParams(p, { replace: true });
              }}
            >
              {n} дн.
            </button>
          ))}
          {/* Где задавали за период — отбором в «Чатах»: переписка и звонки клиента целиком, сделка рядом. */}
          {чаты && (
            <>
              <Link to={`/chats?kbq=${d.id}&days=${days}`} className={`${pill(false)} ml-2`} title="Чаты, где клиенты задавали этот вопрос, — каждый открывается на сообщении-вопросе">
                Чаты · {d.chats}
              </Link>
              <Link to={`/chats?view=calls&kbq=${d.id}&days=${days}`} className={pill(false)} title="Звонки, где клиенты задавали этот вопрос: запись, расшифровка и вся история клиента">
                Звонки · {d.calls}
              </Link>
            </>
          )}
        </div>
      </header>

      {!редактор && (
        <section>
          <h2 className="text-[13px] font-semibold text-fg">Как спрашивают клиенты</h2>
          <Hits id={d.id} days={days} cards={false} step={3} />
        </section>
      )}

      {d.kind && ПОДСКАЗКА[d.kind] && (
        <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn/10 px-3 py-2 text-[12.5px] text-fg">{ПОДСКАЗКА[d.kind]}</div>
      )}

      <section className={`${box} p-3`}>
        <div className="mb-2 flex flex-wrap items-baseline gap-2">
          <h2 className="text-[13px] font-semibold text-fg">Мой ответ</h2>
          <span className="text-[11.5px] text-fg-faint">как вы отвечаете клиенту · видите вы и редактор</span>
        </div>
        <AnswerForm
          key={d.id}
          questionId={d.id}
          autoRecord={answerMode === "voice"}
          autoFocus={answerMode === "text"}
          hotkeys
          position={queue && pos >= 0 ? `${pos + 1} из ${queue.ids.length}` : undefined}
          onPrev={queue && pos > 0 ? () => go(pos - 1) : undefined}
          onNext={queue && pos >= 0 ? () => go(pos + 1) : undefined}
          nextLabel={queue && pos === queue.ids.length - 1 ? "Завершить очередь" : "Следующий →"}
        />
      </section>

      <section className="space-y-3">
        <h2 className="text-[13px] font-semibold text-fg">Ответ на вопрос</h2>
        <div>
          <div className="mb-1 text-[12px] text-fg-muted">Карточки знаний · {d.cards.length}</div>
          {d.cards.length === 0 ? (
            <div className="text-[12.5px] text-fg-faint">Карточки пока нет — её соберём по ответам сотрудников.</div>
          ) : (
            <div className="space-y-2">
              {d.cards.map((c) => (
                <details key={c.id} className={`${box} px-3 py-2`}>
                  <summary className="cursor-pointer list-none">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <Link to={`/knowledge/${c.id}`} className="text-[13px] font-medium text-fg hover:underline" onClick={(e) => e.stopPropagation()}>
                        {c.title}
                      </Link>
                      <span className="text-[11.5px] text-fg-faint">
                        #{c.id} · v{c.version} · {KINDS[c.kind] ?? c.kind}
                      </span>
                      <SyncBadge c={c} />
                    </div>
                    <div className="mt-1 line-clamp-2 text-[12.5px] text-fg-muted">{c.body}</div>
                  </summary>
                  <div className="mt-2 whitespace-pre-wrap border-t border-border pt-2 text-[12.5px] text-fg">{c.body}</div>
                </details>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex flex-wrap items-baseline gap-2 text-[12px] text-fg-muted">
            <span>{редактор ? "Ответы сотрудников" : "Мои прежние ответы"} · {прочие.length}</span>
            {редактор && прочие.length > 0 && (
              <span className={отобрано ? "text-accent" : "text-fg-faint"}>
                отобрано для карточки: {отобрано} — карточку соберём в чате по отобранным
              </span>
            )}
          </div>
          {answers.error && <div className="text-[12px] text-err">{errText(answers.error)}</div>}
          {прочие.length === 0 && !answers.isLoading && (
            <div className="text-[12.5px] text-fg-faint">{редактор ? "Сотрудники ещё не отвечали." : "Других ответов нет."}</div>
          )}
          <AnswerList answers={прочие} meId={me?.id} canSelect={редактор} />
        </div>
      </section>

    </div>
  );
}
